import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getZonedNow, useMinuteTick } from "./NowLine";

// Availability overlays for the week and day grids. Shades BUSY time -- each
// visible day minus an /open-blocks result -- and leaves open time clear.
// Display-only: never reaches the engine (see FEATURE_TRACKER.md,
// Availability Matrix). Two instances per grid; WHAT each shades comes from
// its data and busyBeforeNow, HOW it looks from the class props (index.css
// names treatments, not layers):
//   - tasks-queue x availability: the intersect set, busyBeforeNow (the
//     tasks queue can't use the past), HATCH treatment (.avail-hatch-*,
//     pattern chosen in Settings via hatchType);
//   - just-looking x availability: the refavail calendar set, past time
//     shown like any other time, FILL treatment (.avail-fill-*).
// (Swapped 2026-09-27; before that the just-looking layer was hatched.)
//
// One shape per contiguous busy run per day column, positioned from the
// grid's measured cell geometry (like NowLine), so a multi-hour block reads
// as a single shape instead of one fragment per hour cell. Every shape
// offsets its background by its own position in the grid, so a hatch
// pattern is one continuous pattern anchored to the grid: no seams at
// hour-cell borders, and it lines up across adjacent day columns.

const DAY_MINUTES = 1440;

// Busy runs per visible day, as minutes-of-day [start, end): the whole day
// minus its open time, in the account timezone -- each open-block instant is
// mapped through the same Intl source the grid and now line use
// (getZonedNow), so DST days are handled per instant. No min-duration
// filter: a real 5-minute gap stays open. null openBlocks (no refavail
// calendar selected) means no overlay at all, not an all-busy day.
// `nowZoned` (a getZonedNow result, optional) makes everything before that
// minute busy: earlier days entirely, today up to the minute.
// Exported for unit tests.
export function computeBusyByDay(dayKeys, openBlocks, tz, nowZoned = null) {
  const busy = {};
  if (!openBlocks) return busy;
  if (dayKeys.length === 0) return busy;

  // Cheap pre-filter on raw instants: only blocks that can touch the
  // visible days (a day of slack either side covers any timezone).
  const lo = new Date(`${dayKeys[0]}T00:00:00Z`).getTime() - 2 * 86400000;
  const hi = new Date(`${dayKeys[dayKeys.length - 1]}T00:00:00Z`).getTime() + 3 * 86400000;
  const open = {};
  dayKeys.forEach((dk) => { open[dk] = []; });

  for (const block of openBlocks) {
    const startMs = new Date(block.start).getTime();
    const endMs = new Date(block.end).getTime();
    if (endMs <= lo || startMs >= hi) continue;
    const s = getZonedNow(new Date(startMs), tz);
    const e = getZonedNow(new Date(endMs), tz);
    for (const dk of dayKeys) {
      if (e.dateKey < dk || s.dateKey > dk) continue;
      const startMin = s.dateKey < dk ? 0 : s.hour * 60 + s.minute;
      const endMin = e.dateKey > dk ? DAY_MINUTES : e.hour * 60 + e.minute;
      if (endMin > startMin) open[dk].push([startMin, endMin]);
    }
  }

  for (const dk of dayKeys) {
    let intervals = open[dk];
    if (nowZoned && dk < nowZoned.dateKey) {
      intervals = [];
    } else if (nowZoned && dk === nowZoned.dateKey) {
      const nowMin = nowZoned.hour * 60 + nowZoned.minute;
      intervals = intervals
        .map(([s, e]) => [Math.max(s, nowMin), e])
        .filter(([s, e]) => e > s);
    }
    intervals.sort((a, b) => a[0] - b[0]);
    const runs = [];
    let cursor = 0;
    for (const [s, e] of intervals) {
      if (s > cursor) runs.push([cursor, s]);
      cursor = Math.max(cursor, e);
    }
    if (cursor < DAY_MINUTES) runs.push([cursor, DAY_MINUTES]);
    busy[dk] = runs;
  }
  return busy;
}

// Rendered as the last child of a position:relative grid container.
// `getCellTestId(dateKey, hour)` maps to that hour's cell; `testIdPrefix`
// names the shapes (e.g. "taskqueue-unavail-week"). `layerClassName` /
// `shapeClassName` pick the treatment (required, no layer-specific default).
export default function AvailabilityOverlay({
  tz, dayKeys, openBlocks, getCellTestId, testIdPrefix,
  layerClassName,
  shapeClassName,
  busyBeforeNow = false,
  // Settings "Tasks-queue hatch" (hatched instance only; null for the
  // fill): "vertical" is the base class's own pattern; "dots"/"diagonal"
  // add a .avail-hatch--* modifier (index.css).
  hatchType = null,
}) {
  const shapeClass = hatchType && hatchType !== "vertical"
    ? `${shapeClassName} ${shapeClassName}--${hatchType}`
    : shapeClassName;
  const layerRef = useRef(null);
  const [shapes, setShapes] = useState([]);
  const dayKeysSig = dayKeys.join(",");

  // Only the busyBeforeNow instance tracks the clock, so the shaded edge
  // advances each minute between /open-blocks fetches.
  const [now, setNow] = useState(() => (busyBeforeNow ? getZonedNow(new Date(), tz) : null));
  useEffect(() => {
    setNow(busyBeforeNow ? getZonedNow(new Date(), tz) : null);
  }, [busyBeforeNow, tz]);
  useMinuteTick(() => {
    if (busyBeforeNow) setNow(getZonedNow(new Date(), tz));
  });

  const busyByDay = useMemo(
    () => computeBusyByDay(dayKeys, openBlocks, tz, now),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dayKeysSig, openBlocks, tz, now]
  );

  useLayoutEffect(() => {
    const container = layerRef.current?.parentElement;
    if (!container) return undefined;
    const measure = () => {
      const c = container.getBoundingClientRect();
      const next = [];
      for (const dk of dayKeys) {
        const runs = busyByDay[dk] || [];
        if (runs.length === 0) continue;
        const rectFor = (hour) => {
          const el = container.querySelector(`[data-testid="${getCellTestId(dk, hour)}"]`);
          return el ? el.getBoundingClientRect() : null;
        };
        const first = rectFor(0);
        if (!first) continue;
        const cache = {};
        const y = (min) => {
          if (min >= DAY_MINUTES) {
            const last = cache[23] || (cache[23] = rectFor(23));
            return last.bottom - c.top;
          }
          const h = Math.floor(min / 60);
          const r = cache[h] || (cache[h] = rectFor(h));
          return r.top - c.top + ((min % 60) / 60) * r.height;
        };
        runs.forEach(([s, e], idx) => {
          const top = y(s);
          const bottom = y(e);
          if (bottom - top <= 0) return;
          next.push({
            key: `${dk}-${idx}`,
            dateKey: dk,
            idx,
            startMin: s,
            endMin: e,
            top,
            left: first.left - c.left,
            width: first.width,
            height: bottom - top,
          });
        });
      }
      setShapes(next);
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(container);
    return () => ro?.disconnect();
    // getCellTestId is a fresh closure each parent render; dayKeysSig +
    // busyByDay capture everything that changes the shapes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busyByDay, dayKeysSig]);

  return (
    <div ref={layerRef} className={layerClassName} aria-hidden="true">
      {shapes.map((s) => (
        <div
          key={s.key}
          className={shapeClass}
          data-hatch={hatchType || undefined}
          data-testid={`${testIdPrefix}-${s.dateKey}-${s.idx}`}
          data-start-min={s.startMin}
          data-end-min={s.endMin}
          style={{
            top: `${s.top}px`,
            left: `${s.left}px`,
            width: `${s.width}px`,
            height: `${s.height}px`,
            // Anchor the pattern tile to the grid's origin, not this shape's.
            backgroundPosition: `${-s.left}px ${-s.top}px`,
          }}
        />
      ))}
    </div>
  );
}
